import * as React from 'react';
import Button from '@mui/material/Button';
import { alpha, styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import Iconify from '../components/Iconify';
import { Box, Chip, Stack, TextField, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useState } from 'react';
import constants from '../constants';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { getUsageAccessInfo } from '../services/helper';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    width: '100%',
    maxWidth: 640,
    borderRadius: 24,
    overflow: 'hidden',
  },
  '& .MuiDialogContent-root': {
    padding: 0,
  },
  '& .MuDialogActions-root': {
    padding: theme.spacing(2.5, 3, 3),
  },
}));

export default function AddCommandPopup({ open, onClose, onSave, commands, initialCommand, helperText }) {
  const { t } = useLanguage();
  const { currentUser } = useAuth();
  const [disabled, setDisabled] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [match, setMatch] = React.useState('');
  const [desc, setDesc] = React.useState('');
  const [useComputer, setUseComputer] = React.useState(false);
  const [error, setError] = useState({ isError: false, helperText: '' });
  const newCommandLabel = t('acp.newCommand'); // New Command
  const sayThisLabel = t('acp.sayThis'); // Say this
  const toTypeThisLabel = t('acp.toType'); // To type this
  const saveLabel = t('save'); // Save
  const cancelLabel = t('cancel'); // Cancel
  const gptcheckLabel = t('gptcheckLabel'); // Feed this command text into ChatGPT to receive a response

  React.useEffect(() => {
    if (!open) {
      return;
    }
    if (initialCommand) {
      setMatch(initialCommand.match || '');
      setDesc(initialCommand.description || '');
      setUseComputer(Boolean(initialCommand.useComputer));
      setError({ isError: false, helperText: '' });
      return;
    }
    setMatch('');
    setDesc('');
    setUseComputer(false);
    setError({ isError: false, helperText: '' });
  }, [open, initialCommand]);

  const handleClose = () => {
    onClose();
  };

  const doSave = () => {
    const commandsCount = Array.isArray(currentUser.commands) ? currentUser.commands.length : 0;
    const accessInfo = getUsageAccessInfo(currentUser);
    if (match === '' || desc === '' || error.isError || (!accessInfo.paid && commandsCount >= constants.freeCustomCommands)) {
      return;
    }
    setLoading(true);
    setDisabled(true);
    const command = {
      match: match.trim().toLowerCase(),
      description: desc,
      useComputer,
    };
    onSave(command).then((result) => {
      setLoading(false);
      setDisabled(false);
      if (!result?.keepOpen) {
        setMatch('');
        setDesc('');
      }
    });
  };

  const onChangeCommand = (e) => {
    const nextMatch = e.target.value;
    setMatch(nextMatch);
    const allMatches = [];
    for (const cmd of commands) {
      if (Array.isArray(cmd.match)) {
        allMatches.push(...cmd.match);
      } else {
        allMatches.push(cmd.match);
      }
    }
    if (allMatches.map((item) => item.toLowerCase()).includes(nextMatch.trim().toLowerCase())) {
      setError({ isError: true, helperText: 'Please enter unique command!' });
    } else {
      setError({ isError: false, helperText: '' });
    }
  };

  return (
      <BootstrapDialog aria-labelledby="new-command-dialog" open={open} onClose={handleClose}>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 14,
            top: 14,
            zIndex: 1,
            color: 'common.white',
            bgcolor: alpha('#FFFFFF', 0.1),
            '&:hover': { bgcolor: alpha('#FFFFFF', 0.18) },
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Box
            sx={{
              p: { xs: 3, md: 4 },
              color: 'common.white',
              background: 'linear-gradient(135deg, #171717 0%, #23221F 58%, #0F3D28 100%)',
            }}
          >
            <Stack spacing={2}>
              <Chip
                label="Voice shortcut"
                color="secondary"
                sx={{ alignSelf: 'flex-start', fontWeight: 800 }}
              />
              <Box>
                <Typography id="new-command-dialog" variant="h3" sx={{ color: 'common.white' }}>
                  {newCommandLabel}
                </Typography>
                <Typography sx={{ mt: 1, color: alpha('#FFFFFF', 0.72), lineHeight: 1.8 }}>
                  {helperText || 'Teach Voice Typing what to listen for, then define exactly what should be inserted.'}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Stack spacing={3} sx={{ p: { xs: 2.5, md: 3 } }}>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: 2,
                    bgcolor: 'secondary.lighter',
                    color: 'secondary.dark',
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <Iconify icon="solar:microphone-3-bold-duotone" width={20} height={20} />
                </Box>
                <Box>
                  <Typography variant="subtitle1">{sayThisLabel}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    The short phrase you will speak.
                  </Typography>
                </Box>
              </Stack>
              <TextField
                fullWidth
                error={error.isError}
                helperText={error.helperText || 'Example: follow up with client'}
                value={match}
                onChange={onChangeCommand}
                placeholder="Say a short command"
              />
            </Stack>

            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: 2,
                    bgcolor: 'background.neutral',
                    color: 'text.primary',
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <Iconify icon="solar:keyboard-bold-duotone" width={20} height={20} />
                </Box>
                <Box>
                  <Typography variant="subtitle1">{toTypeThisLabel}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    The text or instruction inserted when the command matches.
                  </Typography>
                </Box>
              </Stack>
              <TextField
                fullWidth
                multiline
                rows={5}
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                placeholder="Type the phrase, template, or instruction here"
              />
            </Stack>

            <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'background.default', border: (theme) => `1px solid ${theme.palette.divider}` }}>
              <FormGroup>
                <FormControlLabel
                  control={<Checkbox size="medium" />}
                  label={gptcheckLabel}
                  checked={useComputer}
                  onChange={() => {
                    setUseComputer(!useComputer);
                  }}
                />
              </FormGroup>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={disabled} color="inherit">
            {cancelLabel}
          </Button>
          <Button autoFocus variant="contained" onClick={doSave} disabled={disabled}>
            {loading ? <CircularProgress size={30} color="secondary" /> : saveLabel}
          </Button>
        </DialogActions>
      </BootstrapDialog>
  );
}
